import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import DeviceShowcase from './DeviceShowcase';

const KineticHero = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const yText = useTransform(scrollYProgress, [0, 1], ['0%', '14%']);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-white via-white to-[#f4f4f6]"
      data-testid="hero-section"
    >
      {/* Soft ambient wash */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[1200px] h-[700px] rounded-full bg-[#C8102E]/[0.03] blur-[180px]" />
      </div>

      <div className="relative z-10 w-full container mx-auto px-6 pt-28 pb-16 flex flex-col items-center text-center">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-2 font-mono text-[11px] md:text-xs uppercase tracking-[0.25em] text-gray-400 mb-6"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#C8102E] animate-pulse" />
          Hei Web Creations · Singapore
        </motion.div>

        {/* Copy block — parallax on scroll */}
        <motion.div style={{ y: yText }} className="flex flex-col items-center">
          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="font-extrabold text-[#1A1A1A] tracking-[-0.035em] leading-[0.98] text-5xl sm:text-6xl lg:text-7xl max-w-4xl"
          >
            Premium websites,<br />built smarter with{' '}
            <span className="text-[#C8102E]">AI.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 text-lg md:text-xl text-gray-500 leading-relaxed max-w-2xl"
          >
            We fuse human design expertise with AI-powered workflows to build
            stunning, conversion-focused websites — faster and more affordably.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-col sm:flex-row items-center gap-4"
          >
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  className="group bg-[#1A1A1A] hover:bg-[#C8102E] text-white px-8 py-6 text-base rounded-full transition-all duration-300"
                  data-testid="hero-primary-cta"
                >
                  Start Your Project
                  <ArrowUpRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
              </motion.div>
            </Link>
            <Link to="/services">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  variant="ghost"
                  className="text-[#1A1A1A] hover:bg-gray-100 px-6 py-6 text-base rounded-full transition-all duration-300"
                  data-testid="hero-secondary-cta"
                >
                  View Our Services →
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </motion.div>

        {/* Device showcase — centered, scroll into view */}
        <div className="mt-16 lg:mt-20 w-full flex justify-center">
          <DeviceShowcase />
        </div>

        {/* Minimal stat strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.4 }}
          className="mt-14 flex items-center gap-8 md:gap-12 font-mono text-xs text-gray-400"
        >
          <span><span className="text-[#1A1A1A] font-semibold">150+</span> projects</span>
          <span><span className="text-[#1A1A1A] font-semibold">98%</span> satisfaction</span>
          <span className="hidden sm:inline"><span className="text-[#1A1A1A] font-semibold">50%</span> faster delivery</span>
        </motion.div>
      </div>
    </section>
  );
};

export default KineticHero;
